"use client";

import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { PMTiles, Protocol } from "pmtiles";
import { env } from "@/lib/env";
import styles from "./location.module.css";
import { useListingContext } from "../listingContex";
import { getReverseGeocode } from "./actions";
import { toast } from "react-toastify";
import { useAppLocale } from "@/hooks/useAppLocale";

const mapUrl = env.NEXT_MAP_URL;

export default function MapLibreSelector() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const locale = useAppLocale();

  const { data, updateField, updateMultipleFields } = useListingContext();
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const initMap = async () => {
      const response = await fetch("/map/my-style.json");
      const style = await response.json();

      // 👅 Determine user locale (example: "fr" from "fr-FR")
      const userLocale = locale;

      // 🪄 Universal expression for label fallback
      const nameExpression = [
        "coalesce",
        ["get", `name:${userLocale}`],
        ["get", "name:en"],
        ["get", "name"],
      ];

      // 🧩 Inject locale expression into all text-based layers
      style.layers = style.layers.map((layer: any) => {
        const layout = layer.layout;
        if (!layout) return layer;

        const field = layout["text-field"];

        // Case 1: string placeholders like "{name}"
        if (typeof field === "string" && field.includes("{name")) {
          layout["text-field"] = nameExpression;
        }

        // Case 2: expression using ["get", "name"]
        if (Array.isArray(field) && field[0] === "get" && field[1] === "name") {
          layout["text-field"] = nameExpression;
        }

        return layer;
      });

      // 1. Setup PMTiles
      const protocol = new Protocol();
      maplibregl.addProtocol("pmtiles", (params, cb) =>
        protocol.tile(params, cb),
      );
      new PMTiles(`${mapUrl}/base/world.pmtiles`);
      new PMTiles(`${mapUrl}/3d/world3d.pmtiles`);

      // 3. Determine Initial Center (Store -> CloudFront API -> Fallback)
      let initialLat = 44.426829;
      let initialLon = 26.102215;
      let initialZoom = 5;

      if (
        data?.map_location &&
        typeof data.map_location.lat === "number" &&
        !isNaN(data.map_location.lat) &&
        typeof data.map_location.long === "number" &&
        !isNaN(data.map_location.long) &&
        data.map_location.lat != 0 &&
        data.map_location.long != 0
      ) {
        initialLat = data.map_location.lat;
        initialLon = data.map_location.long;
        initialZoom = 11; // Start zoomed in if location exists
      } else {
        // Attempt to get location from our CloudFront API
        try {
          const locRes = await fetch("/api/location");
          const locData = await locRes.json();
          if (
            locData &&
            typeof locData.lat === "number" &&
            typeof locData.lon === "number"
          ) {
            initialLat = locData.lat;
            initialLon = locData.lon;
            initialZoom = 11; // City level zoom
          }
        } catch (e) {
          console.warn("Silent CloudFront location fetch failed", e);
        }
      }

      // 4. Initialize Map
      const map = new maplibregl.Map({
        container: mapContainer.current!,
        style,
        center: [initialLon, initialLat],
        zoom: initialZoom,
        minZoom: 3,
        maxZoom: 20,
        pitch: initialZoom >= 15 ? 35 : 0, // Set initial pitch if zoomed in
        bearing: 0,
        attributionControl: false,
        pitchWithRotate: true, // Allow rotation for 3D
        hash: false,
        fadeDuration: 0,
        trackResize: true,
        renderWorldCopies: false,
      });

      mapRef.current = map;

      // 5. Initialize Marker
      const el = document.createElement("div");
      el.className = styles.customMarker;
      const initialMarker = new maplibregl.Marker({
        element: el,
        anchor: "bottom",
      });

      if (data?.map_location) {
        initialMarker.setLngLat([initialLon, initialLat]).addTo(map);
      }
      markerRef.current = initialMarker;

      // 6. Handle Map Click
      map.on("click", (e) => {
        const { lng, lat } = e.lngLat;

        if (!markerRef.current?.getLngLat()) {
          markerRef.current?.addTo(map);
        }
        markerRef.current?.setLngLat([lng, lat]);

        updateField("map_location", { lat, long: lng });
        updateField("exact_map_location", { lat, long: lng });
        triggerReverseGeocode(lat, lng);
      });

      // 7. Handle 3D Tilt on Zoom
      map.on("zoomend", () => {
        const zoom = map.getZoom();
        const currentPitch = map.getPitch();

        // If zoomed IN past 15 and map is flat, tilt it
        if (zoom >= 15 && currentPitch === 0) {
          map.easeTo({
            pitch: 55,
            duration: 600,
            easing: (t) => t * (2 - t),
          });
        }

        // If zoomed OUT below 15 and map is tilted, flatten it
        if (zoom < 15 && currentPitch > 0) {
          map.easeTo({
            pitch: 0,
            bearing: 0, // Reset rotation when zooming out
            duration: 600,
            easing: (t) => t * (2 - t),
          });
        }
      });
    };

    initMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerReverseGeocode = (lat: number, lon: number) => {
    setIsResolving(true);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      try {
        const res = await getReverseGeocode(lon, lat);
        if (!res.success || !res.data) {
          toast("Unable to get location data.", { type: "error" });
          setIsResolving(false);
          return;
        }

        const addressesJson = res.data;

        if (addressesJson.ResultItems && addressesJson.ResultItems.length > 0) {
          const addr = addressesJson.ResultItems[0].Address;
          console.log("Resolved AWS Address:", addr);

          // 1. Build the Untouchable Native Street Line
          let nativeStreet = "";

          if (addr.StreetComponents && addr.StreetComponents.length > 0) {
            const type = addr.StreetComponents[0].Type ?? "";
            const baseName = addr.StreetComponents[0].BaseName ?? "";
            if (type) nativeStreet += type;
            if (baseName) nativeStreet += (nativeStreet ? " " : "") + baseName;
          } else if (addr.Block) {
            nativeStreet += addr.Block;
          }

          if (addr.AddressNumber) {
            nativeStreet += (nativeStreet ? ", " : "") + addr.AddressNumber;
          }
          if (addr.District) {
            nativeStreet += (nativeStreet ? ", " : "") + addr.District;
          }

          // 2. Build the fallback English address (for the draft UI only)
          let fallbackAddressEn = nativeStreet;
          if (addr.Locality)
            fallbackAddressEn +=
              (fallbackAddressEn ? ", " : "") + addr.Locality;
          if (addr.Region?.Name)
            fallbackAddressEn +=
              (fallbackAddressEn ? ", " : "") + addr.Region.Name;
          if (addr.Country?.Name)
            fallbackAddressEn +=
              (fallbackAddressEn ? ", " : "") + addr.Country.Name;

          // 3. Update state with our clean, simplified payload
          updateMultipleFields({
            native_street_line: nativeStreet,
            city: { en: addr.Locality || "" },
            country: { en: addr.Country?.Name || "" },
            region: { en: addr.Region?.Name || "" },
            sub_region: addr.SubRegion?.Name || "",
            country_code: addr.Country?.Code2 || "",
            address: { en: fallbackAddressEn }, // Save full string as draft
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsResolving(false);
      }
    }, 3000);
  };

  return (
    <div className={styles.mapOuterWrapper}>
      <div ref={mapContainer} className={styles.mapCanvas} />

      {isResolving && (
        <div className={styles.resolvingBadge}>
          <div className={styles.miniSpinner}></div>
          Resolving address...
        </div>
      )}
    </div>
  );
}
// 127.0.0.1 doomum.com http://doomum.com/
// 127.0.0.1 www.doomum.com http://www.doomum.com/
// 127.0.0.1 doomum.ro http://doomum.ro/
// 127.0.0.1 doomum.de http://doomum.de/
// 127.0.0.1 doomum.fr http://doomum.fr/
// 127.0.0.1 doomum.es http://doomum.es/
// 127.0.0.1 doomum.it http://doomum.it/
// 127.0.0.1 doomum.nl http://doomum.nl/
// 127.0.0.1 doomum.be http://doomum.be/
// 127.0.0.1 doomum.pl http://doomum.pl/
// 127.0.0.1 doomum.pt http://doomum.pt/
// 127.0.0.1 doomum.dk http://doomum.dk/
// 127.0.0.1 doomum.at http://doomum.at/
// 127.0.0.1 doomum.hu http://doomum.hu/
// 127.0.0.1 doomum.gr http://doomum.gr/
// 127.0.0.1 doomum.tr http://doomum.tr/
// 127.0.0.1 doomum.qa http://doomum.qa/
// 127.0.0.1 doomum.ae http://doomum.ae/
// 127.0.0.1 doomum.co.uk http://doomum.co.uk/
// 127.0.0.1 www.doomum.com http://www.doomum.com/
// 127.0.0.1 www.doomum.ro http://www.doomum.ro/
// 127.0.0.1 www.doomum.de http://www.doomum.de/
// 127.0.0.1 www.doomum.fr http://www.doomum.fr/
// 127.0.0.1 www.doomum.es http://www.doomum.es/
// 127.0.0.1 www.doomum.it http://www.doomum.it/
// 127.0.0.1 www.doomum.nl http://www.doomum.nl/
// 127.0.0.1 www.doomum.be http://www.doomum.be/
// 127.0.0.1 www.doomum.pl http://www.doomum.pl/
// 127.0.0.1 www.doomum.pt http://www.doomum.pt/
// 127.0.0.1 www.doomum.dk http://www.doomum.dk/
// 127.0.0.1 www.doomum.at http://www.doomum.at/
// 127.0.0.1 www.doomum.hu http://www.doomum.hu/
// 127.0.0.1 www.doomum.gr http://www.doomum.gr/
// 127.0.0.1 www.doomum.tr http://www.doomum.tr/
// 127.0.0.1 www.doomum.qa http://www.doomum.qa/
// 127.0.0.1 www.doomum.ae http://www.doomum.ae/
// 127.0.0.1 www.doomum.co.uk http://www.doomum.co.uk/
// 127.0.0.1 hi.doomum.com http://hi.doomum.com/
// 127.0.0.1 no.doomum.com http://no.doomum.com/
// 127.0.0.1 fi.doomum.com http://fi.doomum.com/
// 127.0.0.1 ru.doomum.com http://ru.doomum.com/
// 127.0.0.1 sv.doomum.com http://sv.doomum.com/
// 127.0.0.1 ko.doomum.com http://ko.doomum.com/
// 127.0.0.1 bg.doomum.com http://bg.doomum.com/
// 127.0.0.1 hr.doomum.com http://hr.doomum.com/
// 127.0.0.1 zh.doomum.com http://zh.doomum.com/
// 127.0.0.1 cs.doomum.com http://cs.doomum.com/
// 127.0.0.1 uk.doomum.com http://uk.doomum.com/
// 127.0.0.1 ja.doomum.com http://ja.doomum.com/
// 127.0.0.1 sk.doomum.com http://sk.doomum.com/
// 127.0.0.1 sl.doomum.com http://sl.doomum.com/
// 127.0.0.1 sr.doomum.com http://sr.doomum.com/
